'use client'

import {useEffect} from 'react'
import {Download, X} from 'lucide-react'

type VideoPlayerOverlayProps = {
    /** The streaming video URL. */
    src: string
    /** Download filename shown in the browser's save dialog. */
    downloadName?: string
    onClose: () => void
}

/**
 * Full-screen video player shown when the user clicks a chat bubble's play
 * button. Layout mirrors ImagePreviewOverlay: black backdrop, centered
 * content, top-right close + download. Click outside the <video> dismisses.
 */
export function VideoPlayerOverlay({src, downloadName, onClose}: VideoPlayerOverlayProps) {
    useEffect(() => {
        function onKey(event: KeyboardEvent) {
            if (event.key === 'Escape') onClose()
        }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [onClose])

    return (
        <div
            className="fixed inset-0 z-preview bg-black/90 flex items-center justify-center cursor-zoom-out"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
        >
            <video
                src={src}
                controls
                autoPlay
                preload="metadata"
                className="max-w-[90%] max-h-[90%] cursor-default bg-black"
                onClick={(event) => event.stopPropagation()}
            />

            <div
                className="absolute top-6 right-6 flex items-center gap-3"
                onClick={(event) => event.stopPropagation()}
            >
                <a
                    href={src}
                    download={downloadName || 'video.mp4'}
                    className="text-white/80 hover:text-white flex items-center gap-1.5 text-sm"
                    title="下载视频"
                >
                    <Download size={22}/>
                </a>
                <button
                    type="button"
                    className="text-white/80 hover:text-white"
                    onClick={onClose}
                    aria-label="关闭预览"
                >
                    <X size={28}/>
                </button>
            </div>
        </div>
    )
}
